"use client";

import { navItems } from "@/data/items";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState, useEffect } from "react";
import ServicesDropdown from "./menu";
import { Button } from "./ui/button";
import { useUser } from "@/context/user-context";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const pathname = usePathname();
  const { isAuthenticated, user } = useUser();

  const isHome = pathname === "/";
  const isDark = isHome && !scrolled;

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 8);
    }
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <nav
        className={`fixed top-0 z-50 w-full max-w-7xl py-2 transition-all duration-300 ${
          isDark
            ? "bg-[#0F2929]"
            : "bg-white shadow-md border-b border-gray-100"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-16">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link href="/" className="flex items-center space-x-3 group">
              <div className="h-10 flex items-center justify-center">
                <img
                  src="/logo-logo.jpg"
                  alt="Hexad Logo"
                  className="h-full rounded-lg object-cover"
                />
              </div>
              {/* <span className="text-white font-bold text-xl hidden sm:inline">
                Hexad
              </span> */}
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {[
                { href: "/", label: "Home" },
                { href: "/about", label: "About Us" },
                { href: "/team", label: "Team" },
              ].map(({ href, label }) => (
                <Link
                  key={href}
                  href={href}
                  className={`nav-button ${pathname === href ? "active" : ""} ${!isDark ? "text-gray-800! hover:text-[#0092B8]!" : ""}`}
                >
                  {label}
                </Link>
              ))}
              <ServicesDropdown isDark={isDark} />
              {[
                { href: "/projects", label: "Projects" },
                // { href: "/why-choose-us", label: "Why Choose Us" },
                { href: "/contact", label: "Contact" },
              ].map(({ href, label }) => (
                <Link
                  key={href}
                  href={href}
                  className={`nav-button ${pathname === href ? "active" : ""} ${!isDark ? "text-gray-800! hover:text-[#0092B8]!" : ""}`}
                >
                  {label}
                </Link>
              ))}
            </div>

            {/* Desktop Right Elements */}
            <div className="hidden lg:flex items-center space-x-4">
              {isAuthenticated ? (
                <Link href="/profile">
                  <div className="flex items-center justify-center w-10 h-10 bg-[#0092B8] text-white rounded-full p-2 font-bold hover:scale-105 transition-transform">
                    {user?.firstName?.[0]}
                    {user?.lastName?.[0]}
                  </div>
                </Link>
              ) : (
                <Link href="/auth/login">
                  <Button
                    variant="outline"
                    className={`px-5 py-2 rounded-full font-semibold transition-all bg-transparent ${
                      isDark
                        ? "border-white/80 text-white hover:bg-white hover:text-[#0F2929]"
                        : "border-gray-400 text-gray-800 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    Login
                  </Button>
                </Link>
              )}
              <button
                onClick={() => (window.location.href = "/request-quote")}
                className="px-6 py-2.5 bg-[#0092B8] hover:bg-[#007a9a] text-white text-sm font-bold uppercase tracking-wider rounded-full transition-all shadow-sm hover:-translate-y-0.5 whitespace-nowrap"
              >
                Request a Quote
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`lg:hidden p-2 transition-colors ${isDark ? "text-white hover:text-white/70" : "text-gray-800 hover:text-gray-600"}`}
              aria-label="Menu"
            >
              <svg
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {/* MOBILE MENU */}
      {isOpen && (
        <div className="fixed inset-0 z-50 lg:hidden flex">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
            onClick={() => setIsOpen(false)}
          />

          {/* Sidebar */}
          <div className="relative ml-auto h-screen w-full max-w-xs sm:max-w-sm bg-white shadow-xl transform transition-transform duration-300 ease-in-out flex flex-col overflow-y-auto animate-in slide-in-from-right">
            {/* Header */}
            <div className="shrink-0 flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-white">
              <Link
                href="/"
                onClick={() => setIsOpen(false)}
                className="flex items-center space-x-3"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-[#0F2929] rounded-full">
                  <img
                    src="/logo-logo.jpg"
                    alt="Hexad Logo"
                    className="w-full h-full object-cover rounded-full"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="font-bold text-gray-900 text-base sm:text-lg">
                    Hexad
                  </span>
                  <span className="text-xs text-gray-500 -mt-1">
                    Humanity Counts
                  </span>
                </div>
              </Link>

              <button
                onClick={() => setIsOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close menu"
              >
                <svg
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-4 sm:p-6 space-y-6">
                {/* Navigation */}
                <div className="space-y-2">
                  <h3 className="text-xs font-semibold text-gray-500 px-4 uppercase tracking-wider">
                    Navigation
                  </h3>

                  {navItems.slice(0, 6).map((item) => {
                    const isActive = pathname === item.href;
                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        onClick={() => setIsOpen(false)}
                        className={`flex items-center px-4 py-3 text-sm rounded-lg transition-all group ${
                          isActive
                            ? "bg-[#0092B8]/20 text-[#0F2929] font-semibold"
                            : "text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        <span className="mr-3 text-gray-500 group-hover:text-[#0F2929]">
                          {item.icon}
                        </span>
                        <span className="flex-1">{item.label}</span>
                        <svg
                          width="16"
                          height="16"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          className="opacity-50"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </Link>
                    );
                  })}
                </div>

                {/* Quick Actions — mirrors desktop right section */}
                <div className="space-y-2">
                  <h3 className="text-xs font-semibold text-gray-500 px-4 uppercase tracking-wider">
                    Quick Actions
                  </h3>
                  {[navItems.find((i) => i.href === "/auth/login")!].map((item) => {
                    const isActive = pathname === item.href;
                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        onClick={() => setIsOpen(false)}
                        className={`flex items-center px-4 py-3 text-sm rounded-lg transition-all group ${
                          isActive
                            ? "bg-[#0092B8]/20 text-[#0F2929] font-semibold"
                            : "text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        <span className="mr-3 text-gray-500 group-hover:text-[#0F2929]">
                          {item.icon}
                        </span>
                        <span className="flex-1">{item.label}</span>
                        <svg
                          width="16"
                          height="16"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          className="opacity-50"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Footer CTA */}
            <div className="shrink-0 p-4 sm:p-6 border-t border-gray-200 bg-gray-50 space-y-3">
              <button
                onClick={() => {
                  setIsOpen(false);
                  window.location.href = "/request-quote";
                }}
                className="w-full h-12 bg-[#0092B8] hover:bg-[#007a9a] text-white text-sm font-bold uppercase tracking-wider rounded-full transition-all hover:-translate-y-0.5"
              >
                Request a Quote
              </button>
              {isAuthenticated ? (
                <Link href="/profile" onClick={() => setIsOpen(false)}>
                  <div className="flex items-center justify-center gap-3 p-3 bg-white rounded-lg border border-gray-200 hover:border-[#0092B8] transition-colors">
                    <div className="flex items-center justify-center w-10 h-10 bg-[#0092B8] text-white rounded-full font-bold">
                      {user?.firstName?.[0]}
                      {user?.lastName?.[0]}
                    </div>
                    <span className="font-medium text-gray-700">
                      {user?.firstName} {user?.lastName}
                    </span>
                  </div>
                </Link>
              ) : (
                <Link href="/auth/login" onClick={() => setIsOpen(false)}>
                  <Button
                    variant="outline"
                    className="w-full py-3 border-gray-300 hover:border-[#0092B8] hover:bg-[#0092B8]/10"
                  >
                    Login to Account
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
